import { Component } from '@angular/core';
import { routerTransition } from '../../router.animations';
import { ActivatedRoute, Router } from '@angular/router';
import { KycService } from '@app/core/kyc.service';
import { AgentInfo } from '@app/core/models/agent';
import { ToastrService } from 'ngx-toastr';
import { onConfigData } from '@app/features/coverage/coverage';
import { Title } from "@angular/platform-browser";
import { forkJoin } from 'rxjs/observable/forkJoin';

import { environment } from 'environments/environment';
import { SingpassService } from '@app/core/singpass.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  animations: [routerTransition()],
  host: { '[@routerTransition]': '' }
})
export class HomeComponent {
  isGettingAgentInfo: boolean;
  kycType: any;
  backgroundImage = '../../assets/images/background/bg-age.jpg';
  buttonStyle = {};
  startLabel = "Register Manually";
  info = {};
  allBenefitsLink = "https://www.wellnex-singapore.com/benefits";
  brandName = "Wellnex";
  brandNameLink = "https://www.wellnex-singapore.com/";
  footerItems;
  agentTenantId;
  buttonTextColor = '#000000';
  backgroundColor = '#4d4d4d';
  isQuickKyc = environment.isQuickKyc;
  isWhatsAppReg = false;
  isLoading = false;
  errorMessage: string = null;
  showErrorModal: boolean = false;
  
  readonly singpassManualInputMessage = 'To join, cancel and select "Register Manually".';

  get singpassErrorBody(): string {
    return this.errorMessage
      ? `${this.singpassManualInputMessage}\n${this.errorMessage}`
      : this.singpassManualInputMessage;
  }


  private setError(msg: string) {
    this.errorMessage = msg;
    this.showErrorModal = true;
  }

  closeError() {
    this.showErrorModal = false;
    this.errorMessage = null;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private _kycService: KycService,
    private toastrService: ToastrService,
    private titleService: Title,
    private singpassService: SingpassService,
  ) {
    this.isGettingAgentInfo = false;
    this.kycType = 0;
    route.queryParams.subscribe(params => {
      const referralCode = params['referralCode'] || params['referralcode'];
      if (referralCode) {
        window.localStorage.setItem('referralCode', referralCode);
      } else {
        window.localStorage.setItem('referralCode', 'A0000');
      }

      const customerCode = params['customerUserId'] || params['customeruserid'];
      if (customerCode) {
        window.localStorage.setItem('customerCode', customerCode);
      } else {
        window.localStorage.setItem('customerCode', '');
      }

      const campaign = params['campaign'];
      if (campaign) {
        window.localStorage.setItem('campaign', campaign);
      } else {
        window.localStorage.setItem('campaign', '');
      }

      const registerType = params['registerType'] || params['registertype'];
      if (registerType == 4 && this.isQuickKyc) {
        this._kycService.isWhatsAppRegister = true;
        this.isWhatsAppReg = true;
      }

      const referralCodeValue = window.localStorage.getItem('referralCode');
      const campaignCodeValue = window.localStorage.getItem('campaign');
      const customerCodeValue = window.localStorage.getItem('customerCode');

      if (campaignCodeValue !== "") {
        forkJoin(this._kycService.getCampaignCustomer(referralCodeValue, campaignCodeValue), this._kycService.getInfo()).subscribe((res) => this.getCustomer(res, 'campaign'))
      } else {
        forkJoin(this._kycService.getCustomer2(referralCodeValue, customerCodeValue), this._kycService.getInfo()).subscribe((res) => this.getCustomer(res, 'customer'))
      }

      const onetoken = params['onetoken'];
      if (onetoken) {
        this.handlerSingPassCallBack(onetoken);
      }
    });
  }

  startJourney() {
    if (this._kycService.serverError) {
      this.toastrService.error('Cannot connect to the server!');
      return;
    }

    let tenant = this._kycService.getTenant();
    this.router.navigateByUrl('/' + tenant + '/journey/profile');
    if (this._kycService.isWhatsAppRegister) {
      this._kycService.kyc.profile.emailInvalid = true;
    }
  }

  addMyInfo() {
    if (this._kycService.serverError) {
      this.isLoading = false;
      this.setError('Cannot connect to the server!');
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    const tenantId = this._kycService.kycInfo && this._kycService.kycInfo.TenantId
      ? this._kycService.kycInfo.TenantId
      : '';
    const redirectUri = (environment as any).singpassRedirectUri || `${environment.infoUrl}/api/SingPass/CallBack`;

    this.singpassService.initLogin(tenantId, redirectUri).subscribe(
      result => {
        this.isLoading = false;
        if (result && result.authorizationUrl) {
          // Redirect browser to Singpass login page
          window.location.href = result.authorizationUrl;
        } else {
          this.setError('Could not get Singpass login URL. Please try again.');
        }
      },
      err => {
        console.error('Singpass init error', err);
        this.isLoading = false;
        this.setError('Singpass is currently unavailable. Please try again later.');
      }
    );
  }

  handlerSingPassCallBack(oneToken: string) {
    if (oneToken) {
      this.singpassService.getOneTimeToken(oneToken).subscribe(
        payload => {
          const accessToken = (payload.AccessToken || payload.accessToken) as string;
          this.singpassService.getMyInfo(accessToken).subscribe(
            profile => {
              this._kycService.customerProfile = profile;
              const tenant = this.route.snapshot.paramMap.get('tenant') || this._kycService.getTenant() || 'wellnex';
              this.router.navigateByUrl(`/${tenant}/journey/profile`);
            },
            err => {
              this.setError('Could not fetch login result. Please try again.');
            }
          );
        },
        err => {
          this.isLoading = false;
          this.setError('Could not fetch login result. Please try again.');
        }
      );
      return;
    }

    this.isLoading = false;
    this.setError('Invalid callback. Please try logging in again.');
  }

  getCustomer(res, type) {
    const customerCodeValue = window.localStorage.getItem('customerCode');
    const referralCodeValue = window.localStorage.getItem('referralCode');

    const rs = res[0].data.Content[0].Data;
    const agentInfo = new AgentInfo();
    agentInfo.name = referralCodeValue == 'A0000' ? 'Admin' : `${rs.FirstName} ${rs.LastName}`;
    agentInfo.id = rs.AgentId

    if (type == 'campaign') {
      if (rs == null) {
        this.toastrService.error('Invalid RefNumber!');
        return;
      }
      if (rs.CampaignId == null) {
        this.toastrService.error('Invalid Campaign!');
        return;
      }
      agentInfo.campaignId = rs.CampaignId;
      agentInfo.kycType = 2;
    } else if (type == 'customer') {
      if (customerCodeValue !== '' && rs == null) {
        this.toastrService.error('Invalid Referral Client');
        return;
      }
      agentInfo.customerId = rs.ReferralCustomerId;
    }

    this.agentTenantId = rs.AgentTenantId;
    this._kycService.agentInfo = agentInfo;
    this._kycService.agentInfoObservable.next(agentInfo);
    this._kycService.kyc.agentInfo = agentInfo;

    //this.isGettingAgentInfo = true;
    this._kycService.kyc.coverage.questionCoverages = onConfigData(
      rs.KYCCoverages
    );
    this._kycService.kyc.needs.questionNeedAssessments = onConfigData(
      rs.KYCNeedAssessments
    );
    this._kycService.kyc.coverage.questionRiskProfiles = onConfigData(
      rs.KYCRiskProfiles
    );
    this._kycService.kyc.save.save.map(item => {
      if (item.key === 'needAssessments') {
        item.optionsCheckbox = rs.KYCNeedAssessments;
      }
    });
    this.getKYCInfo(res[1].data);
  }

  getKYCInfo(data) {
    const rs = data.Content[0].Data;
    this._kycService.kycInfo = rs;
    this.titleService.setTitle(rs.Title);
    if (rs.AllBenefitsLink) {
      this.allBenefitsLink = rs.AllBenefitsLink || this.allBenefitsLink;
    }
    if (rs.BrandName) {
      this.brandName = rs.BrandName || this.brandName;
    }
    if (rs.BrandNameLink) {
      this.brandNameLink = rs.BrandNameLink || this.brandNameLink;
    }
    if (rs.BackgroundImageId) {
      this._kycService.getImage(rs.BackgroundImageId).subscribe(({ data }) => {
        if (data.Content) {
          this.backgroundImage = 'data:image/png;base64,' + data.Content;
        }
      })
    }
    if (rs.ButtonBackgroundColor) {
      this.buttonStyle = { 'backgroundColor': rs.ButtonBackgroundColor }
    }
    if (rs.ButtonTextColor) {
      this.buttonStyle = { ...this.buttonStyle, 'color': rs.ButtonTextColor }
    }
    if (rs.FavIconId) {
      this._kycService.getImage(rs.FavIconId).subscribe(({ data }) => {
        if (data.Content) {
          let imageURL = 'data:image/png;base64,' + data.Content;
          let favIcon: HTMLLinkElement = document.querySelector('#favicon') as HTMLLinkElement;
          favIcon.href = imageURL;
        }
      })
    }
    if (rs.LogoId) {
      this._kycService.getImage(rs.LogoId).subscribe(({ data }) => {
        if (data.Content) {
          let imageURL = 'data:image/png;base64,' + data.Content;
          this._kycService.logoImageObservable.next(imageURL);
        }
      })
    }
    this.footerItems =
      [
        {
          value: "",
          className: "text-left",
          link: ""
        },
        {
          value: rs.Footer || "Copyright @ My-Insurer Pte Ltd - Singapore",
          className: "text-center"
        },
        {
          value: "Terms & Conditions",
          className: "text-right",
          link: rs.TcLink || "http://www.my-insurer.net/term--conditions-users.html"
        }
      ];

    if (this.agentTenantId !== rs.TenantId) {
      this.toastrService.error('This RefNumber does not belong this company!');
    } else {
      this.isGettingAgentInfo = true;
    }
    this.buttonTextColor = rs.ButtonTextColor;
    this.backgroundColor = rs.HeaderColor;
    this._kycService.logoImageObservable.next(null);
  }

  get showAddMyInfoButton(): boolean {
    if (!this.isGettingAgentInfo) return false;
    if (this._kycService.isWhatsAppRegister) return false;
    const info = this._kycService.kycInfo || {};
    const rt = info.RegisterType || info.registerType || info.RegistrationType || info.RegistrationMode || info.registerBy;
    if (rt === undefined || rt === null || rt === '') {
      return true;
    }
    if (typeof rt === 'number') return rt === 1;
    const srt = String(rt).toLowerCase();
    return srt.indexOf('email') !== -1 || srt === '1';
  }
}



// WEBPACK FOOTER //
// ./src/app/features/home/home.component.ts